import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BackofficeEmailDelivery, BackofficeLicense, BackofficeUser } from './backoffice.models';
import { BackofficeService } from './backoffice.service';
import { PUBLIC_LEGAL_URLS } from '../../core/legal/public-legal.constants';

type CompanyPlanAllocationSummary = {
  key: string;
  name: string;
  purchased: number;
  assigned: number;
  remaining: number;
  licenses: BackofficeLicense[];
  selectable: boolean;
  optionLabel: string;
  previewLicenseId: string;
};

@Component({
  selector: 'app-backoffice-users-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './users-page.component.html',
  styleUrl: './users-page.component.css'
})
export class BackofficeUsersPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly backofficeService = inject(BackofficeService);
  readonly privacyPolicyUrl = PUBLIC_LEGAL_URLS.privacyPolicy;
  readonly termsOfServiceUrl = PUBLIC_LEGAL_URLS.termsOfService;

  readonly overview = this.backofficeService.overview;
  readonly users = computed(() => [...this.backofficeService.users()].sort((a, b) => a.firstName.localeCompare(b.firstName)));
  readonly companies = this.backofficeService.companies;
  readonly productAccessOptions = computed(() =>
    this.backofficeService.productAccessOptions().filter((option) => option.key !== 'none')
  );
  readonly currentCompanyId = this.backofficeService.currentCompanyId;
  readonly canManageCompanies = this.backofficeService.userCanManageCompanies;
  readonly isSuperadminMode = computed(() => this.overview()?.scope === 'superadmin');
  readonly pageEyebrow = computed(() => this.isSuperadminMode() ? 'Admins' : 'Users');
  readonly pageTitle = computed(() => this.isSuperadminMode() ? 'Piloter les admins de compagnie' : 'Piloter les utilisateurs finaux');
  readonly pageCreateLabel = computed(() => this.isSuperadminMode() ? 'Nouvel admin' : 'Nouvel utilisateur');
  readonly roleLabel = computed(() => this.isSuperadminMode() ? 'Company admin' : 'User');

  readonly selectedUserId = signal<string | null>(null);
  readonly selectedCompanyFormId = signal('');
  readonly selectedPlanKey = signal('');
  readonly saving = signal(false);
  readonly actionUserId = signal<string | null>(null);
  readonly feedback = signal<string | null>(null);
  readonly feedbackType = signal<'success' | 'error'>('success');
  readonly formFeedback = signal<string | null>(null);
  readonly formFeedbackType = signal<'success' | 'error'>('success');

  readonly activeCount = computed(() => this.users().filter((user) => user.status === 'active').length);
  readonly pendingCount = computed(() => this.users().filter((user) => user.invitationStatus === 'pending').length);
  readonly billableCount = computed(() => this.users().filter((user) => user.consumesSeat).length);
  readonly companyCoverageCount = computed(() => new Set(this.users().map((user) => user.companyId)).size);
  readonly selectedUser = computed(() =>
    this.users().find((user) => user.id === this.selectedUserId()) ?? null
  );
  readonly selectedCompany = computed(() => {
    const selectedCompanyId = this.selectedCompanyFormId() || this.currentCompanyId() || this.companies()[0]?.id || '';
    return this.companies().find((company) => company.id === selectedCompanyId) ?? null;
  });
  readonly assignedLicenseByUserId = computed(() => {
    const licensesByUserId = new Map<string, string>();

    for (const company of this.companies()) {
      Object.values(company.licenseSummaryByPlan || {}).forEach((summary) => {
        (summary.licenses || []).forEach((license) => {
          if (license.assignedUserId) {
            licensesByUserId.set(license.assignedUserId, license.id);
          }
        });
      });
    }

    return licensesByUserId;
  });
  readonly companyPlanSummaries = computed<CompanyPlanAllocationSummary[]>(() => {
    const company = this.selectedCompany();
    const currentUser = this.selectedUser();
    if (!company) {
      return [];
    }

    return this.productAccessOptions()
      .map((plan) => {
        const summary = company.licenseSummaryByPlan?.[plan.key];
        const licenses = summary?.licenses || [];
        const purchased = Number(summary?.purchased || company.seatQuotaByPlan?.[plan.key] || 0);
        const assigned = Number(summary?.assigned || company.usedSeatsByPlan?.[plan.key] || 0);
        const remaining = Number(summary?.remaining || company.remainingSeatsByPlan?.[plan.key] || 0);
        const userAlreadyAssignedToPlan = currentUser?.assignedPlanKey === plan.key;
        const previewLicenseId = this.resolvePreviewLicenseId(licenses, currentUser?.id || '');

        return {
          key: plan.key,
          name: plan.name,
          purchased,
          assigned,
          remaining,
          licenses,
          selectable: remaining > 0 || userAlreadyAssignedToPlan,
          optionLabel: `${plan.name} · ${remaining} restante(s) / ${purchased} achetee(s)`,
          previewLicenseId,
        };
      })
      .filter((plan) => plan.purchased > 0 || plan.assigned > 0 || plan.remaining > 0 || currentUser?.assignedPlanKey === plan.key);
  });
  readonly selectablePlanSummaries = computed(() => this.companyPlanSummaries());
  readonly selectedPlanSummary = computed(() =>
    this.companyPlanSummaries().find((plan) => plan.key === this.selectedPlanKey()) ?? null
  );

  readonly form = this.fb.nonNullable.group({
    id: [''],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    identifier: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    companyId: [''],
    assignedPlanKey: ['', Validators.required],
    sendInvitation: [true]
  });

  constructor() {
    this.form.controls.companyId.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.selectedCompanyFormId.set(value || '');

        if (this.isSuperadminMode()) {
          return;
        }

        const currentPlanKey = this.form.controls.assignedPlanKey.value;
        const visiblePlanKeys = new Set(this.companyPlanSummaries().map((plan) => plan.key));
        if (!visiblePlanKeys.has(currentPlanKey)) {
          this.form.controls.assignedPlanKey.setValue(
            this.companyPlanSummaries().find((plan) => plan.selectable)?.key || ''
          );
        }
      });

    this.form.controls.assignedPlanKey.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.selectedPlanKey.set(value || ''));

    effect(() => {
      this.companies();
      this.currentCompanyId();

      if (!this.selectedUserId()) {
        this.syncDefaultCompany();
      }
    });

    effect(() => {
      const assignedPlanControl = this.form.controls.assignedPlanKey;
      if (this.isSuperadminMode()) {
        assignedPlanControl.setValidators([]);
        assignedPlanControl.setValue('', { emitEvent: false });
      } else {
        assignedPlanControl.setValidators([Validators.required]);
      }
      assignedPlanControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.backofficeService.loadOverview();
    this.resetForm();
  }

  startCreate({ preserveFormFeedback = false }: { preserveFormFeedback?: boolean } = {}): void {
    this.selectedUserId.set(null);
    this.feedback.set(null);
    if (!preserveFormFeedback) {
      this.formFeedback.set(null);
    }
    this.resetForm();
  }

  editUser(user: BackofficeUser): void {
    this.selectedUserId.set(user.id);
    this.feedback.set(null);
    this.formFeedback.set(null);
    this.form.setValue({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      identifier: user.identifier,
      email: user.email,
      companyId: user.companyId,
      assignedPlanKey: user.assignedPlanKey || '',
      sendInvitation: false
    });
  }

  submit(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    if (!this.isSuperadminMode() && !rawValue.assignedPlanKey) {
      this.formFeedbackType.set('error');
      this.formFeedback.set('Une licence doit etre selectionnee pour chaque utilisateur.');
      return;
    }

    const companyId = this.canManageCompanies()
      ? rawValue.companyId || this.currentCompanyId() || this.companies()[0]?.id || ''
      : this.currentCompanyId() || this.companies()[0]?.id || '';

    this.formFeedback.set(null);
    this.saving.set(true);

    this.backofficeService.saveUser({
      id: rawValue.id || undefined,
      firstName: rawValue.firstName.trim(),
      lastName: rawValue.lastName.trim(),
      identifier: rawValue.identifier.trim(),
      email: rawValue.email.trim(),
      companyId,
      organizationRoleKey: this.isSuperadminMode() ? 'company_admin' : 'user',
      assignedPlanKey: this.isSuperadminMode() ? '' : rawValue.assignedPlanKey,
      status: 'active',
      sendInvitation: rawValue.id ? rawValue.sendInvitation : true
    }).subscribe({
      next: ({ result }) => {
        this.saving.set(false);
        this.formFeedbackType.set('success');
        this.formFeedback.set(this.buildSaveSuccessMessage({
          savedUser: result?.user,
          invitationEmail: result?.invitationEmail,
          wasUpdate: Boolean(rawValue.id),
        }));
        this.startCreate({ preserveFormFeedback: true });
      },
      error: (error) => {
        this.saving.set(false);
        this.formFeedbackType.set('error');
        this.formFeedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  deleteUser(user: BackofficeUser): void {
    const confirmed = window.confirm(`Supprimer definitivement ${user.firstName} ${user.lastName} ? Cette action est irreversible.`);
    if (!confirmed) {
      return;
    }

    this.actionUserId.set(user.id);
    this.feedback.set(null);

    this.backofficeService.deleteUser(user.id).subscribe({
      next: () => {
        this.actionUserId.set(null);
        this.feedbackType.set('success');
        this.feedback.set('Utilisateur supprime.');
      },
      error: (error) => {
        this.actionUserId.set(null);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  resendInvitation(user: BackofficeUser): void {
    this.actionUserId.set(user.id);
    this.feedback.set(null);

    this.backofficeService.resendInvitation(user.id).subscribe({
      next: ({ result }) => {
        this.actionUserId.set(null);
        this.feedbackType.set('success');
        this.feedback.set(this.buildInvitationDeliveryMessage(
          result?.invitationEmail,
          'Invitation renvoyee.',
          'Invitation renvoyee a'
        ));
      },
      error: (error) => {
        this.actionUserId.set(null);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  sendPasswordReset(user: BackofficeUser): void {
    this.actionUserId.set(user.id);
    this.feedback.set(null);

    this.backofficeService.sendPasswordReset(user.id).subscribe({
      next: ({ result }) => {
        this.actionUserId.set(null);
        this.feedbackType.set('success');
        this.feedback.set(this.buildInvitationDeliveryMessage(
          result?.resetPasswordEmail,
          'Email de reset envoye.',
          'Email de reset envoye a'
        ));
      },
      error: (error) => {
        this.actionUserId.set(null);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  trackUser(_: number, user: BackofficeUser): string {
    return user.id;
  }

  trackPlanSummary(_: number, plan: CompanyPlanAllocationSummary): string {
    return plan.key;
  }

  resolveAssignedLicenseId(user: BackofficeUser): string {
    return this.assignedLicenseByUserId().get(user.id) || '';
  }

  private resetForm(): void {
    this.form.reset({
      id: '',
      firstName: '',
      lastName: '',
      identifier: '',
      email: '',
      companyId: this.currentCompanyId() || this.companies()[0]?.id || '',
      assignedPlanKey: this.isSuperadminMode() ? '' : this.companyPlanSummaries().find((plan) => plan.selectable)?.key || '',
      sendInvitation: true
    });
  }

  private syncDefaultCompany(): void {
    const defaultCompanyId = this.currentCompanyId() || this.companies()[0]?.id || '';
    if (!defaultCompanyId) {
      return;
    }

    if (!this.canManageCompanies()) {
      this.form.controls.companyId.setValue(defaultCompanyId);
      return;
    }

    if (!this.form.controls.companyId.value) {
      this.form.controls.companyId.setValue(defaultCompanyId);
    }
  }

  private formatError(code: string | undefined): string {
    if ((code || '').startsWith('EMAIL_SEND_FAILED')) {
      return 'Le compte n a pas ete finalise car l email n a pas pu etre envoye.';
    }

    switch (code) {
      case 'EMAIL_PROVIDER_NOT_CONFIGURED':
        return 'L envoi d emails n est pas configure sur le backend.';
      case 'EMAIL_FROM_NOT_CONFIGURED':
        return 'L adresse d expedition email n est pas configuree.';
      case 'IDENTIFIER_ALREADY_EXISTS':
        return 'Cet identifiant existe deja.';
      case 'EMAIL_ALREADY_EXISTS':
        return 'Cet email existe deja.';
      case 'NO_LICENSE_AVAILABLE_FOR_PLAN':
        return 'Aucune licence disponible pour ce plan.';
      case 'LICENSE_PLAN_REQUIRED':
        return 'Une licence doit etre attribuee a chaque compte.';
      case 'CANNOT_DELETE_CURRENT_USER':
        return 'Vous ne pouvez pas supprimer votre propre compte.';
      case 'INVALID_USER_STATUS':
        return 'Le backoffice ne permet que des comptes actifs.';
      case 'SUPERADMIN_ROLE_FORBIDDEN':
        return 'Ce role est reserve au superadmin.';
      case 'SUPERADMIN_CAN_ONLY_MANAGE_COMPANY_ADMINS':
        return 'Le superadmin ne gere que des admins de compagnie.';
      case 'SUPERADMIN_CANNOT_MANAGE_STANDARD_USERS':
        return 'Le superadmin ne gere pas les utilisateurs finaux.';
      case 'ADMIN_CAN_ONLY_MANAGE_STANDARD_USERS':
        return 'Un admin ne peut gerer que des utilisateurs standards de sa compagnie.';
      case 'MISSING_USER_FIELDS':
        return 'Tous les champs obligatoires doivent etre remplis.';
      default:
        return 'L action sur l utilisateur a echoue.';
    }
  }

  private buildSaveSuccessMessage({
    savedUser,
    invitationEmail,
    wasUpdate
  }: {
    savedUser?: Partial<BackofficeUser>;
    invitationEmail?: BackofficeEmailDelivery;
    wasUpdate: boolean;
  }): string {
    const fullName = `${String(savedUser?.firstName || '').trim()} ${String(savedUser?.lastName || '').trim()}`.trim();
    const targetLabel = this.isSuperadminMode() ? 'Admin' : 'Utilisateur';
    const subject = fullName || savedUser?.email || '';
    const subjectSuffix = subject ? ` ${subject}` : '';
    const baseMessage = wasUpdate
      ? `${targetLabel}${subjectSuffix} mis a jour avec succes.`
      : `${targetLabel}${subjectSuffix} cree avec succes.`;

    if (!invitationEmail) {
      return baseMessage;
    }

    if (!invitationEmail.sent) {
      return `${baseMessage} Email non envoye (${invitationEmail.reason}).`;
    }

    return `${baseMessage} Invitation d activation envoyee a ${invitationEmail.to}.`;
  }

  private buildInvitationDeliveryMessage(
    emailDelivery: BackofficeEmailDelivery | undefined,
    fallbackMessage: string,
    successPrefix: string
  ): string {
    if (!emailDelivery) {
      return fallbackMessage;
    }

    if (!emailDelivery.sent) {
      return `${fallbackMessage} Email non envoye (${emailDelivery.reason}).`;
    }

    return `${successPrefix} ${emailDelivery.to}.`;
  }

  private resolvePreviewLicenseId(licenses: BackofficeLicense[], userId: string): string {
    return licenses.find((license) => license.assignedUserId === userId)?.id
      || licenses.find((license) => license.status === 'available')?.id
      || '';
  }
}
